import React, { useState, useEffect, useCallback } from 'react';
import { Card, Row, Col, Table, Modal, Form, Alert, Spinner } from 'react-bootstrap';
import { Button, CircularProgress } from '@mui/material';
import { Visibility, Lock, Print, Cancel, LockClock, Person } from '@mui/icons-material';
import { ToastContainer, toast } from "react-toastify";
import { FaCalendarAlt, FaMoneyBillWave, FaCreditCard, FaMobile, FaQrcode, FaUniversity, FaGlobe, FaLock, FaUnlock, FaEye } from 'react-icons/fa';
import axios from 'axios';
import { getHeaders } from '../../utility/getHeader';
import { getUserName } from '../../functions/storageUtils';
import { fetchComboData } from '../../services/api';
import fetchData from '../../functions/fetchData';
import useBillingDate from '../../hooks/useBillingDate';
import { BillingDateDisplay } from '../../components/BillingDateProvider';
import Layout from '../../layout/Layout';
import './DayClose.css';

export default function DayClose() {
  const { billingDate, isLoading: isLoadingBillingDate, refreshBillingDate } = useBillingDate();
  const [selectedDate, setSelectedDate] = useState('');
  const [dayCloseData, setDayCloseData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [cashDrawerData, setCashDrawerData] = useState({
    openingCash: 0,
    closingCash: 0,
    cashIn: 0,
    cashOut: 0,
    notes: ''
  });
  const [isDayClosed, setIsDayClosed] = useState(false);
  const [detailedView, setDetailedView] = useState(false);
  const [salesBreakdown, setSalesBreakdown] = useState([]);
  const [paymentOptions, setPaymentOptions] = useState([]);
  const [showReconciliation, setShowReconciliation] = useState(false);
  const [showReconciliationReport, setShowReconciliationReport] = useState(false);
  const [showDayCloseReport, setShowDayCloseReport] = useState(false);
  const [showDebugInfo, setShowDebugInfo] = useState(false);
  const [debugData, setDebugData] = useState({
    allBillsCount: 0,
    billsForDate: 0,
    sampleBills: [],
    fetchQuery: '',
    selectedDateFormatted: ''
  });
  const [reconciliationData, setReconciliationData] = useState({
    dayCloseData: null,
    cashierSummaries: [],
    billsSummary: null,
    discrepancies: [],
    isReconciled: false
  });


  // Enhanced payment method icons mapping with gradients
  const getPaymentIcon = (paymentName) => {
    const name = paymentName.toLowerCase();
    if (name.includes('cash')) return { icon: <FaMoneyBillWave />, color: 'success', gradient: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)' };
    if (name.includes('upi')) return { icon: <FaMobile />, color: 'primary', gradient: 'linear-gradient(135deg, #007bff 0%, #6610f2 100%)' };
    if (name.includes('card') || name.includes('credit') || name.includes('debit')) return { icon: <FaCreditCard />, color: 'info', gradient: 'linear-gradient(135deg, #17a2b8 0%, #6f42c1 100%)' };
    if (name.includes('qr')) return { icon: <FaQrcode />, color: 'warning', gradient: 'linear-gradient(135deg, #ffc107 0%, #fd7e14 100%)' };
    if (name.includes('bank') || name.includes('transfer')) return { icon: <FaUniversity />, color: 'secondary', gradient: 'linear-gradient(135deg, #6c757d 0%, #495057 100%)' };
    if (name.includes('online') || name.includes('net')) return { icon: <FaGlobe />, color: 'danger', gradient: 'linear-gradient(135deg, #dc3545 0%, #e83e8c 100%)' };
    if (name.includes('entertainment')) return { icon: <FaMoneyBillWave />, color: 'purple', gradient: 'linear-gradient(135deg, #6f42c1 0%, #e83e8c 100%)' };
    return { icon: <FaMoneyBillWave />, color: 'dark', gradient: 'linear-gradient(135deg, #343a40 0%, #6c757d 100%)' };
  };

  // Initialize selectedDate with billing date when available
  useEffect(() => {
    if (billingDate && !selectedDate) {
      setSelectedDate(billingDate);
    }
  }, [billingDate, selectedDate]);

  useEffect(() => {
    fetchPaymentOptions();
  }, []);

  const fetchPaymentOptions = async () => {
    try {
      const options = await fetchComboData("paymentoptions", "name");
      setPaymentOptions(options || []);
    } catch (error) {
      console.error('Error fetching payment options:', error);
      // Fallback to default payment methods if API fails
      setPaymentOptions([
        { name: 'Cash' },
        { name: 'UPI' },
        { name: 'Card' },
        { name: 'QR Code' },
        { name: 'Bank Transfer' },
        { name: 'Online' }
      ]);
    }
  };

  const fetchDayCloseData = useCallback(async () => {
    if (!selectedDate) {
      console.log('No selected date, skipping data fetch');
      return;
    }
    
    setLoading(true);
    try {
     // console.log('Fetching data for date:', selectedDate);
      
      // Fetch day close summary
      const summaryData = await fetchData("day_close_summary", null, "id", { close_date: selectedDate });
      // console.log('Day close summary data:', summaryData);

      if (summaryData && summaryData.length > 0) {
        setDayCloseData(summaryData[0]);
        setIsDayClosed(summaryData[0].status === 'closed');
      } else {
        // Calculate current day data if no day close record exists
        await calculateCurrentDayData();
      }

      // Fetch detailed breakdown
      await fetchDetailedBreakdown();

    } catch (error) {
      console.error('Error fetching day close data:', error);
      // If no record exists, calculate from live data
      await calculateCurrentDayData();
    } finally {
      setLoading(false);
    }
  }, [selectedDate, paymentOptions]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (paymentOptions.length > 0 && selectedDate) {
      fetchDayCloseData();
    }
  }, [paymentOptions.length, selectedDate, fetchDayCloseData]);

  const calculateCurrentDayData = async () => {
    try {
     // console.log('Calculating current day data for date:', selectedDate);
      
      // Debug: Let's also check what bills exist without date filter first
      const allBills = await fetchData("bills", null, "id", {});
      //console.log('All bills in database:', allBills?.length || 0, allBills?.slice(0, 3));
      
      // Fetch sales data directly from bills table for the selected date
      const salesData = await fetchData("bills", null, "id", { setup_date: selectedDate });
      //console.log('Bills data fetched for date', selectedDate, ':', salesData?.length || 0, salesData);
      
      // Update debug data for display
      setDebugData({
        allBillsCount: allBills?.length || 0,
        billsForDate: salesData?.length || 0,
        sampleBills: allBills?.slice(0, 5).map(bill => ({
          id: bill.id,
          setup_date: bill.setup_date,
          bill_date: bill.bill_date,
          total_amount: bill.total_amount,
          created_at: bill.created_at
        })) || [],
        fetchQuery: `setup_date = ${selectedDate}`,
        selectedDateFormatted: selectedDate
      });

      if (salesData && salesData.length > 0) {
        console.log('=== RAW SALES DATA ===');
        console.log('Total bills fetched:', salesData.length);
        salesData.forEach(bill => {
          console.log(`Bill ${bill.id}: status="${bill.status}", payment_method="${bill.payment_method}", amount=${bill.total_amount}`);
        });
        
        // Filter out cancelled and refunded bills from sales calculations
        const bills = salesData.filter(bill => 
          bill.status !== 'cancelled' && bill.status !== 'refunded'
        );
        
        console.log('=== AFTER FILTERING ===');
        console.log('Active bills count:', bills.length);
        bills.forEach(bill => {
          console.log(`Active Bill ${bill.id}: payment_method="${bill.payment_method}", amount=${bill.total_amount}`);
        });
        
        // Initialize payment summary with standard payment methods according to SQL table
        const paymentSummary = {
          close_date: selectedDate,
          total_sales: 0,
          cash_sales: 0,
          upi_sales: 0,
          card_sales: 0,
          qr_sales: 0,
          bank_transfer_sales: 0,
          online_sales: 0,
          entertainment_sales: 0,
          other_sales: 0,
          total_orders: bills.length,
          total_items_sold: 0,
          discount_amount: 0,
          tax_amount: 0,
          net_sales: 0,
          status: 'open'
        };

        // Initialize dynamic payment method fields for any additional payment options
        // Map payment option names to correct database column names
        const paymentMethodMapping = {
          'qr code': 'qr_sales',
          'qr': 'qr_sales',
          'bank transfer': 'bank_transfer_sales',
          'credit card': 'card_sales',
          'debit card': 'card_sales'
        };

        paymentOptions.forEach(option => {
          const optionName = option.name.toLowerCase();
          const mappedField = paymentMethodMapping[optionName];
          
          if (mappedField) {
            // Standard payment methods - ensure they exist but don't overwrite
            if (!paymentSummary.hasOwnProperty(mappedField)) {
              paymentSummary[mappedField] = 0;
            }
          } else {
            // Non-standard payment methods - create dynamic field
            const key = `${optionName.replace(/\s+/g, '_')}_sales`;
            if (!paymentSummary.hasOwnProperty(key)) {
              paymentSummary[key] = 0;
            }
          }
        });

        // Calculate totals by payment method
        bills.forEach(bill => {
          const amount = parseFloat(bill.grand_total || bill.paid_amount || bill.total_amount || 0);
          const taxAmount = parseFloat(bill.tax_amount || 0);
          const discountAmount = parseFloat(bill.discount_amount || 0);
          
          // Get payment method from bill (use payment_method field from database)
          const paymentMethod = (bill.payment_method || bill.payment_mode || 'cash').toLowerCase().trim();
          
          console.log('Bill ID:', bill.id, 'Payment method:', paymentMethod, 'Amount:', amount, 'Status:', bill.status);
          
          // Add to total_sales only if NOT entertainment (entertainment is tracked separately)
          if (paymentMethod !== 'entertainment') {
            paymentSummary.total_sales += amount;
          }
          
          paymentSummary.tax_amount += taxAmount;
          paymentSummary.discount_amount += discountAmount;
          
          // Map to standard payment method fields according to SQL table
          if (paymentMethod === 'entertainment') {
            // Entertainment bills should be tracked separately and NOT included in total_sales
            if (!paymentSummary.entertainment_sales) {
              paymentSummary.entertainment_sales = 0;
            }
            paymentSummary.entertainment_sales += amount;
          } else if (paymentMethod.includes('cash') || paymentMethod === 'cash') {
            paymentSummary.cash_sales += amount;
          } else if (paymentMethod.includes('upi') || paymentMethod === 'upi') {
            paymentSummary.upi_sales += amount;
          } else if (paymentMethod.includes('card') || paymentMethod.includes('credit') || paymentMethod.includes('debit') || paymentMethod === 'card') {
            paymentSummary.card_sales += amount;
          } else if (paymentMethod.includes('qr') || paymentMethod === 'qr' || paymentMethod === 'qr code') {
            console.log(`✓ QR PAYMENT DETECTED - Bill ${bill.id}: Adding ${amount} to qr_sales`);
            paymentSummary.qr_sales += amount;
          } else if (paymentMethod.includes('bank') || paymentMethod.includes('transfer') || paymentMethod === 'bank transfer') {
            paymentSummary.bank_transfer_sales += amount;
          } else if (paymentMethod.includes('online') || paymentMethod.includes('net') || paymentMethod === 'online') {
            paymentSummary.online_sales += amount;
          } else {
            // Find matching dynamic payment option
            const matchingOption = paymentOptions.find(option => 
              option.name.toLowerCase().replace(/\s+/g, '_') === paymentMethod.replace(/\s+/g, '_') ||
              option.name.toLowerCase() === paymentMethod ||
              paymentMethod.includes(option.name.toLowerCase())
            );
            
            if (matchingOption) {
              // Use payment method mapping for standard methods
              const paymentMethodMapping = {
                'qr code': 'qr_sales',
                'qr': 'qr_sales',
                'bank transfer': 'bank_transfer_sales',
                'credit card': 'card_sales',
                'debit card': 'card_sales'
              };
              
              const optionName = matchingOption.name.toLowerCase();
              const mappedField = paymentMethodMapping[optionName];
              const key = mappedField || `${optionName.replace(/\s+/g, '_')}_sales`;
              
              if (paymentSummary.hasOwnProperty(key)) {
                paymentSummary[key] += amount;
              } else {
                paymentSummary.other_sales += amount;
              }
            } else {
              // Default to cash if no match found
              paymentSummary.cash_sales += amount;
              console.log('No match found, defaulted to cash. Amount:', amount);
            }
          }
        });

        // Calculate net sales
        paymentSummary.net_sales = paymentSummary.total_sales - paymentSummary.discount_amount;

        console.log('=== FINAL PAYMENT SUMMARY ===');
        console.log('qr_sales:', paymentSummary.qr_sales);
        console.log('cash_sales:', paymentSummary.cash_sales);
        console.log('card_sales:', paymentSummary.card_sales);
        console.log('total_sales:', paymentSummary.total_sales);

        // Fetch total items sold from bill_items if available
        try {
          const itemsData = await fetchData("bill_items", null, "id", { setup_date: selectedDate });
          
          if (itemsData && itemsData.length > 0) {
            paymentSummary.total_items_sold = itemsData.reduce((total, item) => {
              return total + parseFloat(item.quantity || 0);
            }, 0);
          }
        } catch (error) {
          console.error('Error fetching order items:', error);
        }

        //console.log('Final payment summary:', paymentSummary);
        setDayCloseData(paymentSummary);
      } else {
        // No sales data for selected date - create default structure with all required fields
        const defaultSummary = {
          close_date: selectedDate,
          total_sales: 0,
          cash_sales: 0,
          upi_sales: 0,
          card_sales: 0,
          qr_sales: 0,
          bank_transfer_sales: 0,
          online_sales: 0,
          entertainment_sales: 0,
          other_sales: 0,
          total_orders: 0,
          total_items_sold: 0,
          discount_amount: 0,
          tax_amount: 0,
          net_sales: 0,
          status: 'open'
        };
        
        // Initialize additional dynamic payment method fields to 0
        // Map payment option names to correct database column names
        const paymentMethodMapping = {
          'qr code': 'qr_sales',
          'qr': 'qr_sales',
          'bank transfer': 'bank_transfer_sales',
          'credit card': 'card_sales',
          'debit card': 'card_sales'
        };

        paymentOptions.forEach(option => {
          const optionName = option.name.toLowerCase();
          const mappedField = paymentMethodMapping[optionName];
          
          if (mappedField) {
            // Standard payment methods - ensure they exist but don't overwrite
            if (!defaultSummary.hasOwnProperty(mappedField)) {
              defaultSummary[mappedField] = 0;
            }
          } else {
            // Non-standard payment methods - create dynamic field
            const key = `${optionName.replace(/\s+/g, '_')}_sales`;
            if (!defaultSummary.hasOwnProperty(key)) {
              defaultSummary[key] = 0;
            }
          }
        });
        
        setDayCloseData(defaultSummary);
      }
      
    } catch (error) {
      console.error('Error calculating day data:', error);
      toast.error('Error fetching sales data for selected date');
      // Create default structure with all required fields for error case
      const errorSummary = {
        close_date: selectedDate,
        total_sales: 0,
        cash_sales: 0,
        upi_sales: 0,
        card_sales: 0,
        qr_sales: 0,
        bank_transfer_sales: 0,
        online_sales: 0,
        entertainment_sales: 0,
        other_sales: 0,
        total_orders: 0,
        total_items_sold: 0,
        discount_amount: 0,
        tax_amount: 0,
        net_sales: 0,
        status: 'open'
      };
      
      // Initialize additional dynamic payment method fields to 0
      // Map payment option names to correct database column names
      const paymentMethodMapping = {
        'qr code': 'qr_sales',
        'qr': 'qr_sales',
        'bank transfer': 'bank_transfer_sales',
        'credit card': 'card_sales',
        'debit card': 'card_sales'
      };

      paymentOptions.forEach(option => {
        const optionName = option.name.toLowerCase();
        const mappedField = paymentMethodMapping[optionName];
        
        if (mappedField) {
          // Standard payment methods - ensure they exist but don't overwrite
          if (!errorSummary.hasOwnProperty(mappedField)) {
            errorSummary[mappedField] = 0;
          }
        } else {
          // Non-standard payment methods - create dynamic field
          const key = `${optionName.replace(/\s+/g, '_')}_sales`;
          if (!errorSummary.hasOwnProperty(key)) {
            errorSummary[key] = 0;
          }
        }
      });
      
      setDayCloseData(errorSummary);
    }
  };

  const fetchPaymentSummary = async () => {
    try {
      // Get bills for the selected date using setup_date filter
      const bills = await fetchData("final_bill", null, "id", { setup_date: selectedDate }) || [];

      // Calculate summary
      const summary = {
        close_date: selectedDate,
        total_sales: 0,
        cash_sales: 0,
        upi_sales: 0,
        card_sales: 0,
        qr_sales: 0,
        bank_transfer_sales: 0,
        online_sales: 0,
        other_sales: 0,
        total_orders: bills.length,
        total_items_sold: 0,
        status: 'open'
      };

      bills.forEach(bill => {
        const amount = parseFloat(bill.paid_amount || 0);
        summary.total_sales += amount;

        // Group by payment method (if column exists, otherwise assume cash)
        const paymentMethod = bill.payment_method || 'cash';
        switch (paymentMethod) {
          case 'cash':
            summary.cash_sales += amount;
            break;
          case 'upi':
            summary.upi_sales += amount;
            break;
          case 'card':
            summary.card_sales += amount;
            break;
          case 'qr':
            summary.qr_sales += amount;
            break;
          case 'bank_transfer':
            summary.bank_transfer_sales += amount;
            break;
          case 'online':
            summary.online_sales += amount;
            break;
          default:
            summary.other_sales += amount;
        }
      });

      return summary;
    } catch (error) {
      console.error('Error fetching payment summary:', error);
      throw error;
    }
  };

  const fetchDetailedBreakdown = async () => {
    try {
      // Get order items for detailed breakdown using setup_date
      const items = await fetchData("bill_items", null, "id", { setup_date: selectedDate }) || [];

      // Group by item name
      const breakdown = {};
      items.forEach(item => {
        const itemName = item.item_name;
        if (!breakdown[itemName]) {
          breakdown[itemName] = {
            item_name: itemName,
            total_quantity: 0,
            total_amount: 0,
            orders: 0
          };
        }
        breakdown[itemName].total_quantity += parseInt(item.quantity || 0);
        breakdown[itemName].total_amount += parseFloat(item.total_price || 0);
        breakdown[itemName].orders += 1;
      });

      setSalesBreakdown(Object.values(breakdown));
    } catch (error) {
      console.error('Error fetching detailed breakdown:', error);
    }
  };

  const handleDayClose = async () => {
    try {
      setLoading(true);

      // Calculate additional financial details
      const totalSales = parseFloat(dayCloseData?.total_sales || 0);
      const totalOrders = parseInt(dayCloseData?.total_orders || 0);
      const totalItemsSold = parseInt(dayCloseData?.total_items_sold || 0);

      // Calculate tax and discount amounts if available
      let taxAmount = 0;
      let discountAmount = 0;
      let netSales = totalSales;

      try {
        // Fetch bills to calculate tax and discount
        const bills = await fetchData("final_bill", null, "id", { setup_date: selectedDate }) || [];
        
        taxAmount = bills.reduce((sum, bill) => sum + parseFloat(bill.tax_amount || 0), 0);
        discountAmount = bills.reduce((sum, bill) => sum + parseFloat(bill.discount_amount || 0), 0);
        netSales = totalSales - discountAmount;
      } catch (error) {
        console.warn('Could not fetch tax/discount data:', error);
      }

      // Prepare day close data according to SQL table structure
      const closeData = {
        close_date: selectedDate,
        total_sales: totalSales,
        cash_sales: parseFloat(dayCloseData?.cash_sales || 0),
        upi_sales: parseFloat(dayCloseData?.upi_sales || 0),
        card_sales: parseFloat(dayCloseData?.card_sales || 0),
        qr_sales: parseFloat(dayCloseData?.qr_sales || 0),
        bank_transfer_sales: parseFloat(dayCloseData?.bank_transfer_sales || 0),
        online_sales: parseFloat(dayCloseData?.online_sales || 0),
        other_sales: parseFloat(dayCloseData?.other_sales || 0),
        total_orders: totalOrders,
        total_items_sold: totalItemsSold,
        discount_amount: discountAmount,
        tax_amount: taxAmount,
        net_sales: netSales,
        opened_by: null, // This would need to be tracked when day opens
        closed_by: getUserName(),
        opening_time: null, // This would need to be tracked when day opens
        closing_time: new Date().toISOString(),
        status: 'closed',
        notes: cashDrawerData.notes || ''
      };

      // Add any additional dynamic payment methods from paymentOptions
      // Map payment option names to correct database column names
      const paymentMethodMapping = {
        'qr code': 'qr_sales',
        'qr': 'qr_sales',
        'bank transfer': 'bank_transfer_sales',
        'credit card': 'card_sales',
        'debit card': 'card_sales'
      };

      paymentOptions.forEach(option => {
        const optionName = option.name.toLowerCase();
        const mappedField = paymentMethodMapping[optionName];
        
        if (mappedField) {
          // Use mapped field name for standard payment methods
          if (dayCloseData?.[mappedField] !== undefined && !closeData[mappedField]) {
            closeData[mappedField] = parseFloat(dayCloseData[mappedField] || 0);
          }
        } else {
          // Use dynamic field name for non-standard payment methods
          const fieldName = `${optionName.replace(/\s+/g, '_')}_sales`;
          if (dayCloseData?.[fieldName] !== undefined && !closeData[fieldName]) {
            closeData[fieldName] = parseFloat(dayCloseData[fieldName] || 0);
          }
        }
      });

      console.log('Day close data to be saved:', closeData);

      // Save day close summary
      const response = await axios.post(
        `/insertdata/day_close_summary`,
        closeData,
        getHeaders()
      );

      if (response.data.success) {
        toast.success('Day closed successfully!');
        setIsDayClosed(true);
        setShowCloseModal(false);
        
        // Update local state to reflect closed status
        setDayCloseData(prev => ({ 
          ...prev, 
          ...closeData,
          status: 'closed',
          closing_time: closeData.closing_time
        }));
        
        // Optionally refresh data from server
        await fetchDayCloseData();
      } else {
        toast.error('Failed to close day');
      }

    } catch (error) {
      console.error('Error closing day:', error);
      if (error.response?.data?.message) {
        toast.error(`Error closing day: ${error.response.data.message}`);
      } else {
        toast.error('Error closing day. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return `฿ ${parseFloat(amount || 0).toFixed(2)}`;
  };

  // Reconciliation Functions
  const fetchReconciliationData = async () => {
    try {
      setLoading(true);
     // console.log('Fetching reconciliation data for date:', selectedDate);
      
      // 1. Get day close summary data
      const dayCloseSummary = await fetchData("day_close_summary", null, "id", { close_date: selectedDate });
     // console.log('Day close summary:', dayCloseSummary);
      
      // 2. Get cashier summaries for the date
      const cashierSummaries = await fetchData("cashier_summaries", null, "id", { setup_date: selectedDate });
     // console.log('Cashier summaries:', cashierSummaries);
      
      // 3. Get bills summary from bills table
      const bills = await fetchData("bills", null, "id", { setup_date: selectedDate });
      // console.log('Bills for reconciliation:', bills?.length || 0);

      // Calculate bills summary
      const billsSummary = calculateBillsSummary(bills || []);
      
      // Compare and find discrepancies
      const discrepancies = findDiscrepancies(
        dayCloseSummary?.[0] || null,
        cashierSummaries || [],
        billsSummary
      );
      
      setReconciliationData({
        dayCloseData: dayCloseSummary?.[0] || null,
        cashierSummaries: cashierSummaries || [],
        billsSummary,
        discrepancies,
        isReconciled: discrepancies.length === 0
      });
      
    } catch (error) {
      console.error('Error fetching reconciliation data:', error);
      toast.error('Error fetching reconciliation data');
    } finally {
      setLoading(false);
    }
  };

  const calculateBillsSummary = (bills) => {
    // Filter out cancelled and refunded bills from reconciliation calculations
    const activeBills = bills.filter(bill => 
      bill.status !== 'cancelled' && bill.status !== 'refunded'
    );
    
    const summary = {
      total_sales: 0,
      cash_sales: 0,
      upi_sales: 0,
      card_sales: 0,
      qr_sales: 0,
      bank_transfer_sales: 0,
      online_sales: 0,
      entertainment_sales: 0,
      other_sales: 0,
      total_transactions: activeBills.length,
      total_items: 0,
      tax_amount: 0,
      discount_amount: 0
    };

    activeBills.forEach(bill => {
      const amount = parseFloat(bill.paid_amount || bill.total_amount || bill.grand_total || 0);
      const taxAmount = parseFloat(bill.tax_amount || 0);
      const discountAmount = parseFloat(bill.discount_amount || 0);
      
      // Map payment methods
      const paymentMethod = (bill.payment_method || bill.payment_mode || 'cash').toLowerCase().trim();
      
      // Add to total_sales only if NOT entertainment
      if (paymentMethod !== 'entertainment') {
        summary.total_sales += amount;
      }
      
      summary.tax_amount += taxAmount;
      summary.discount_amount += discountAmount;
      
      switch (paymentMethod) {
        case 'cash':
          summary.cash_sales += amount;
          break;
        case 'upi':
          summary.upi_sales += amount;
          break;
        case 'card':
          summary.card_sales += amount;
          break;
        case 'qr':
          summary.qr_sales += amount;
          break;
        case 'bank_transfer':
          summary.bank_transfer_sales += amount;
          break;
        case 'online':
          summary.online_sales += amount;
          break;
        case 'entertainment':
          // Entertainment sales tracked separately but NOT included in total_sales
          summary.entertainment_sales += amount;
          break;
        case '':
        case null:
        case undefined:
          // Treat empty payment method as unknown/other
          summary.other_sales += amount;
          break;
        default:
          summary.other_sales += amount;
      }
    });

    return summary;
  };

  const findDiscrepancies = (dayClose, cashierSummaries, billsSummary) => {
    const discrepancies = [];
    
    if (!dayClose && !billsSummary) {
      return discrepancies;
    }
    
    // If no day close record exists, use bills data as reference
    const referenceData = dayClose || billsSummary;
    
    // 1. Compare Day Close vs Bills
    if (dayClose && billsSummary) {
      const totalSalesDiff = parseFloat(dayClose.total_sales || 0) - parseFloat(billsSummary.total_sales || 0);
      if (Math.abs(totalSalesDiff) > 0.01) {
        discrepancies.push({
          type: 'total_sales',
          source1: 'Day Close',
          source2: 'Bills',
          amount1: dayClose.total_sales,
          amount2: billsSummary.total_sales,
          difference: totalSalesDiff,
          severity: Math.abs(totalSalesDiff) > 100 ? 'high' : 'medium'
        });
      }
      
      // Compare individual payment methods
      const paymentMethods = ['cash_sales', 'upi_sales', 'card_sales', 'qr_sales', 'bank_transfer_sales', 'online_sales', 'other_sales'];
      
      paymentMethods.forEach(method => {
        const dayCloseAmount = parseFloat(dayClose[method] || 0);
        const billsAmount = parseFloat(billsSummary[method] || 0);
        const diff = dayCloseAmount - billsAmount;
        
        if (Math.abs(diff) > 0.01) {
          discrepancies.push({
            type: method,
            source1: 'Day Close',
            source2: 'Bills',
            amount1: dayCloseAmount,
            amount2: billsAmount,
            difference: diff,
            severity: Math.abs(diff) > 50 ? 'medium' : 'low'
          });
        }
      });
    }
    
    // 2. Compare with Cashier Summaries
    if (cashierSummaries && cashierSummaries.length > 0) {
      const totalCashierSales = cashierSummaries.reduce((sum, cashier) => sum + parseFloat(cashier.total_sales || 0), 0);
      const totalCashierCash = cashierSummaries.reduce((sum, cashier) => sum + parseFloat(cashier.cash_total || 0), 0);
      const totalCashierUPI = cashierSummaries.reduce((sum, cashier) => sum + parseFloat(cashier.upi_total || 0), 0);
      const totalCashierCard = cashierSummaries.reduce((sum, cashier) => sum + parseFloat(cashier.card_total || 0), 0);
      const totalCashierQR = cashierSummaries.reduce((sum, cashier) => sum + parseFloat(cashier.qr_total || 0), 0);
      
      const referenceTotalSales = parseFloat(referenceData.total_sales || 0);
      const referenceCashSales = parseFloat(referenceData.cash_sales || 0);
      
      // Compare total sales
      const totalSalesDiff = totalCashierSales - referenceTotalSales;
      if (Math.abs(totalSalesDiff) > 0.01) {
        discrepancies.push({
          type: 'cashier_total_sales',
          source1: 'Cashier Summaries',
          source2: dayClose ? 'Day Close' : 'Bills',
          amount1: totalCashierSales,
          amount2: referenceTotalSales,
          difference: totalSalesDiff,
          severity: Math.abs(totalSalesDiff) > 100 ? 'high' : 'medium'
        });
      }
      
      // Compare cash sales
      const cashSalesDiff = totalCashierCash - referenceCashSales;
      if (Math.abs(cashSalesDiff) > 0.01) {
        discrepancies.push({
          type: 'cashier_cash_sales',
          source1: 'Cashier Summaries',
          source2: dayClose ? 'Day Close' : 'Bills',
          amount1: totalCashierCash,
          amount2: referenceCashSales,
          difference: cashSalesDiff,
          severity: Math.abs(cashSalesDiff) > 50 ? 'medium' : 'low'
        });
      }
    }
    
    return discrepancies;
  };

  const exportToPDF = () => {
    // Create a new window for printing
    const printWindow = window.open('', '_blank');
    
    // Generate print-friendly HTML content
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Day Close Report - ${selectedDate}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              margin: 20px;
              color: #333;
            }
            .header {
              text-align: center;
              margin-bottom: 30px;
              border-bottom: 2px solid #333;
              padding-bottom: 20px;
            }
            .company-name {
              font-size: 24px;
              font-weight: bold;
              margin-bottom: 5px;
            }
            .report-title {
              font-size: 18px;
              margin-bottom: 10px;
            }
            .date {
              font-size: 14px;
              color: #666;
            }
            .summary-section {
              margin-bottom: 30px;
            }
            .summary-grid {
              display: grid;
              grid-template-columns: repeat(2, 1fr);
              gap: 15px;
              margin-bottom: 20px;
            }
            .summary-item {
              border: 1px solid #ddd;
              padding: 15px;
              border-radius: 5px;
              text-align: center;
            }
            .summary-label {
              font-size: 12px;
              color: #666;
              margin-bottom: 5px;
            }
            .summary-value {
              font-size: 18px;
              font-weight: bold;
              color: #333;
            }
            .payment-section {
              margin-bottom: 30px;
            }
            .section-title {
              font-size: 16px;
              font-weight: bold;
              margin-bottom: 15px;
              border-bottom: 1px solid #ccc;
              padding-bottom: 5px;
            }
            .payment-grid {
              display: grid;
              grid-template-columns: repeat(3, 1fr);
              gap: 10px;
              margin-bottom: 20px;
            }
            .payment-item {
              border: 1px solid #ddd;
              padding: 10px;
              border-radius: 3px;
              text-align: center;
            }
            .payment-label {
              font-size: 12px;
              color: #666;
              margin-bottom: 3px;
            }
            .payment-value {
              font-size: 14px;
              font-weight: bold;
            }
            .items-table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 20px;
            }
            .items-table th,
            .items-table td {
              border: 1px solid #ddd;
              padding: 8px;
              text-align: left;
            }
            .items-table th {
              background-color: #f5f5f5;
              font-weight: bold;
            }
            .items-table tr:nth-child(even) {
              background-color: #f9f9f9;
            }
            .total-row {
              font-weight: bold;
              background-color: #e9ecef !important;
            }
            .footer {
              margin-top: 40px;
              text-align: center;
              font-size: 12px;
              color: #666;
            }
            @media print {
              body { margin: 0; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="company-name">View Park</div>
            <div class="report-title">Day Close Report</div>
            <div class="date">Date: ${new Date(selectedDate).toLocaleDateString('en-US', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}</div>
          </div>

          <div class="summary-section">
            <div class="section-title">Sales Summary</div>
            <div class="summary-grid">
              <div class="summary-item">
                <div class="summary-label">Total Sales</div>
                <div class="summary-value">${formatCurrency(dayCloseData?.total_sales || 0)}</div>
              </div>
              <div class="summary-item">
                <div class="summary-label">Total Orders</div>
                <div class="summary-value">${dayCloseData?.total_orders || 0}</div>
              </div>
              <div class="summary-item">
                <div class="summary-label">Items Sold</div>
                <div class="summary-value">${dayCloseData?.total_items_sold || 0}</div>
              </div>
              <div class="summary-item">
                <div class="summary-label">Average Order Value</div>
                <div class="summary-value">${formatCurrency(
                  dayCloseData?.total_orders > 0 
                    ? dayCloseData.total_sales / dayCloseData.total_orders 
                    : 0
                )}</div>
              </div>
            </div>
          </div>

          <div class="payment-section">
            <div class="section-title">Payment Methods Breakdown</div>
            <div class="payment-grid">
              ${generatePaymentMethods().map(method => `
                <div class="payment-item">
                  <div class="payment-label">${method.label}</div>
                  <div class="payment-value">${formatCurrency(dayCloseData?.[method.key] || 0)}</div>
                </div>
              `).join('')}
            </div>
          </div>

          <div class="items-section">
            <div class="section-title">Item-wise Sales Details</div>
            ${salesBreakdown.length > 0 ? `
              <table class="items-table">
                <thead>
                  <tr>
                    <th>S.No</th>
                    <th>Item Name</th>
                    <th>Quantity Sold</th>
                    <th>Total Amount</th>
                    <th>No. of Orders</th>
                    <th>Avg Price per Unit</th>
                  </tr>
                </thead>
                <tbody>
                  ${salesBreakdown.map((item, index) => `
                    <tr>
                      <td>${index + 1}</td>
                      <td>${item.item_name}</td>
                      <td>${item.total_quantity}</td>
                      <td>${formatCurrency(item.total_amount)}</td>
                      <td>${item.orders}</td>
                      <td>${formatCurrency(item.total_amount / item.total_quantity)}</td>
                    </tr>
                  `).join('')}
                  <tr class="total-row">
                    <td colspan="2"><strong>TOTAL</strong></td>
                    <td><strong>${salesBreakdown.reduce((sum, item) => sum + item.total_quantity, 0)}</strong></td>
                    <td><strong>${formatCurrency(salesBreakdown.reduce((sum, item) => sum + item.total_amount, 0))}</strong></td>
                    <td><strong>${salesBreakdown.reduce((sum, item) => sum + item.orders, 0)}</strong></td>
                    <td><strong>-</strong></td>
                  </tr>
                </tbody>
              </table>
            ` : `
              <div style="text-align: center; padding: 40px; color: #666;">
                <p>No sales data found for ${selectedDate}</p>
              </div>
            `}
          </div>

          <div class="footer">
            <p>Generated on: ${new Date().toLocaleString()}</p>
            <p>Report generated by View Park POS System</p>
          </div>
        </body>
      </html>
    `;

    // Write content to the new window and print
    printWindow.document.write(printContent);
    printWindow.document.close();
    
    // Wait for content to load then print
    printWindow.onload = () => {
      printWindow.print();
      printWindow.close();
    };
  };

  // Export Reconciliation Report to PDF
  const exportReconciliationToPDF = () => {
    const printWindow = window.open('', '_blank');
    
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Sale Reconciliation Report - ${selectedDate}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; color: #333; }
            .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 20px; }
            .company-name { font-size: 24px; font-weight: bold; margin-bottom: 5px; }
            .report-title { font-size: 18px; margin-bottom: 10px; }
            .date { font-size: 14px; color: #666; }
            .summary-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 15px; margin-bottom: 30px; }
            .summary-card { border: 1px solid #ddd; padding: 15px; border-radius: 5px; text-align: center; }
            .summary-card.primary { background: #007bff; color: white; }
            .summary-card.success { background: #28a745; color: white; }
            .summary-card.warning { background: #ffc107; color: black; }
            .table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            .table th, .table td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            .table th { background-color: #f5f5f5; font-weight: bold; }
            .alert { padding: 15px; border-radius: 5px; margin: 15px 0; }
            .alert.success { background: #d4edda; border: 1px solid #c3e6cb; color: #155724; }
            .alert.danger { background: #f8d7da; border: 1px solid #f5c6cb; color: #721c24; }
            .badge { padding: 3px 8px; border-radius: 3px; font-size: 12px; }
            .badge.high { background: #dc3545; color: white; }
            .badge.medium { background: #ffc107; color: black; }
            .badge.low { background: #17a2b8; color: white; }
            @media print { body { margin: 0; } }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="company-name">View Park</div>
            <div class="report-title">Sale Reconciliation Report</div>
            <div class="date">Date: ${new Date(selectedDate).toLocaleDateString('en-US', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}</div>
          </div>

          <div class="summary-grid">
            <div class="summary-card primary">
              <h6>Day Close Summary</h6>
              <h4>${formatCurrency(reconciliationData.dayCloseData?.total_sales || 0)}</h4>
            </div>
            <div class="summary-card success">
              <h6>Cashier Summaries</h6>
              <h4>${formatCurrency(reconciliationData.cashierSummaries?.reduce((sum, cashier) => sum + parseFloat(cashier.total_sales || 0), 0) || 0)}</h4>
            </div>
            <div class="summary-card warning">
              <h6>Bills Summary</h6>
              <h4>${formatCurrency(reconciliationData.billsSummary?.total_sales || 0)}</h4>
            </div>
          </div>

          ${reconciliationData.discrepancies && reconciliationData.discrepancies.length > 0 ? `
            <div class="alert danger">
              <h6>Discrepancies Found (${reconciliationData.discrepancies.length})</h6>
              <table class="table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Source 1</th>
                    <th>Source 2</th>
                    <th>Amount 1</th>
                    <th>Amount 2</th>
                    <th>Difference</th>
                    <th>Severity</th>
                  </tr>
                </thead>
                <tbody>
                  ${reconciliationData.discrepancies.map(disc => `
                    <tr>
                      <td>${disc.type.replace('_', ' ')}</td>
                      <td>${disc.source1}</td>
                      <td>${disc.source2}</td>
                      <td>${formatCurrency(disc.amount1)}</td>
                      <td>${formatCurrency(disc.amount2)}</td>
                      <td style="color: ${disc.difference > 0 ? 'green' : 'red'}">${formatCurrency(disc.difference)}</td>
                      <td><span class="badge ${disc.severity}">${disc.severity}</span></td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            </div>
          ` : `
            <div class="alert success">
              <h6>✅ All Reconciled</h6>
              <p>No discrepancies found between data sources.</p>
            </div>
          `}

          <div style="margin-top: 40px; text-align: center; font-size: 12px; color: #666;">
            <p>Generated on: ${new Date().toLocaleString()}</p>
            <p>Report generated by View Park POS System</p>
          </div>
        </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    
    printWindow.onload = () => {
      printWindow.print();
      printWindow.close();
    };
  };

  // Export Day Close Report to PDF
  const exportDayCloseToPDF = () => {
    const printWindow = window.open('', '_blank');
    
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Day Close Report - ${selectedDate}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; color: #333; }
            .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 20px; }
            .company-name { font-size: 24px; font-weight: bold; margin-bottom: 5px; }
            .report-title { font-size: 18px; margin-bottom: 10px; }
            .date { font-size: 14px; color: #666; }
            .metrics-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 15px; margin-bottom: 30px; }
            .metric-card { border: 1px solid #ddd; padding: 15px; border-radius: 5px; text-align: center; background: #f8f9fa; }
            .metric-label { font-size: 12px; color: #666; margin-bottom: 5px; }
            .metric-value { font-size: 18px; font-weight: bold; }
            .payment-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-bottom: 20px; }
            .payment-card { border: 1px solid #ddd; padding: 10px; border-radius: 3px; }
            .table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            .table th, .table td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            .table th { background-color: #f5f5f5; font-weight: bold; }
            .financial-summary { background: #f8f9fa; padding: 15px; border-radius: 5px; margin-top: 20px; }
            .section-title { font-size: 16px; font-weight: bold; margin-bottom: 15px; border-bottom: 1px solid #ccc; padding-bottom: 5px; }
            @media print { body { margin: 0; } }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="company-name">View Park Restaurant</div>
            <div class="report-title">Day Close Summary Report</div>
            <div class="date">Date: ${new Date(selectedDate).toLocaleDateString('en-US', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}</div>
          </div>

          <div class="metrics-grid">
            <div class="metric-card">
              <div class="metric-label">Total Sales</div>
              <div class="metric-value">${formatCurrency(dayCloseData?.total_sales || 0)}</div>
            </div>
            <div class="metric-card">
              <div class="metric-label">Total Orders</div>
              <div class="metric-value">${dayCloseData?.total_orders || 0}</div>
            </div>
            <div class="metric-card">
              <div class="metric-label">Items Sold</div>
              <div class="metric-value">${dayCloseData?.total_items_sold || 0}</div>
            </div>
            <div class="metric-card">
              <div class="metric-label">Average Order</div>
              <div class="metric-value">${formatCurrency(
                dayCloseData?.total_orders > 0 
                  ? dayCloseData.total_sales / dayCloseData.total_orders 
                  : 0
              )}</div>
            </div>
          </div>

          <div class="section-title">Payment Methods Breakdown</div>
          <div class="payment-grid">
            ${generatePaymentMethods().map(method => `
              <div class="payment-card">
                <div class="metric-label">${method.label}</div>
                <div class="metric-value">${formatCurrency(dayCloseData?.[method.key] || 0)}</div>
              </div>
            `).join('')}
          </div>

          ${salesBreakdown && salesBreakdown.length > 0 ? `
            <div class="section-title">Top Selling Items</div>
            <table class="table">
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Item Name</th>
                  <th>Quantity</th>
                  <th>Total Amount</th>
                  <th>Orders</th>
                  <th>Avg Price</th>
                </tr>
              </thead>
              <tbody>
                ${salesBreakdown.slice(0, 10).map((item, index) => `
                  <tr>
                    <td>${index + 1}</td>
                    <td>${item.item_name}</td>
                    <td>${item.total_quantity}</td>
                    <td>${formatCurrency(item.total_amount)}</td>
                    <td>${item.orders}</td>
                    <td>${formatCurrency(item.total_amount / item.total_quantity)}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          ` : ''}

          <div class="financial-summary">
            <div class="section-title">Financial Summary</div>
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px;">
              <div>
                <p><strong>Gross Sales:</strong> ${formatCurrency(dayCloseData?.total_sales || 0)}</p>
                <p><strong>Tax Amount:</strong> ${formatCurrency(dayCloseData?.tax_amount || 0)}</p>
                <p><strong>Discount Amount:</strong> ${formatCurrency(dayCloseData?.discount_amount || 0)}</p>
              </div>
              <div>
                <p><strong>Net Sales:</strong> ${formatCurrency(dayCloseData?.net_sales || 0)}</p>
                <p><strong>Status:</strong> ${isDayClosed ? 'Closed' : 'Open'}</p>
                <p><strong>Closed By:</strong> ${dayCloseData?.closed_by || 'N/A'}</p>
              </div>
            </div>
          </div>

          <div style="margin-top: 40px; text-align: center; font-size: 12px; color: #666;">
            <p>Generated on: ${new Date().toLocaleString()}</p>
            <p>Report generated by View Park POS System</p>
          </div>
        </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    
    printWindow.onload = () => {
      printWindow.print();
      printWindow.close();
    };
  };

  const saveDayClose = async () => {
    if (!dayCloseData) {
      toast.error('No data to save');
      return;
    }

    try {
      setLoading(true);
      
      // Calculate additional financial details
      const totalSales = parseFloat(dayCloseData?.total_sales || 0);
      const totalOrders = parseInt(dayCloseData?.total_orders || 0);
      const totalItemsSold = parseInt(dayCloseData?.total_items_sold || 0);

      // Calculate tax and discount amounts if available
      let taxAmount = parseFloat(dayCloseData?.tax_amount || 0);
      let discountAmount = parseFloat(dayCloseData?.discount_amount || 0);
      let netSales = parseFloat(dayCloseData?.net_sales || totalSales - discountAmount);

      // Prepare complete day close summary data according to SQL table structure
      const dayClosePayload = {
        close_date: selectedDate,
        total_sales: totalSales,
        cash_sales: parseFloat(dayCloseData?.cash_sales || 0),
        upi_sales: parseFloat(dayCloseData?.upi_sales || 0),
        card_sales: parseFloat(dayCloseData?.card_sales || 0),
        qr_sales: parseFloat(dayCloseData?.qr_sales || 0),
        bank_transfer_sales: parseFloat(dayCloseData?.bank_transfer_sales || 0),
        online_sales: parseFloat(dayCloseData?.online_sales || 0),
        other_sales: parseFloat(dayCloseData?.other_sales || 0),
        total_orders: totalOrders,
        total_items_sold: totalItemsSold,
        discount_amount: discountAmount,
        tax_amount: taxAmount,
        net_sales: netSales,
        opened_by: null, // This would need to be tracked when day opens
        closed_by: getUserName(),
        opening_time: null, // This would need to be tracked when day opens  
        closing_time: new Date().toISOString(),
        status: 'closed',
        notes: cashDrawerData.notes || ''
      };

      // Add any additional dynamic payment methods from paymentOptions
      paymentOptions.forEach(option => {
        const fieldName = `${option.name.toLowerCase().replace(/\s+/g, '_')}_sales`;
        if (dayCloseData?.[fieldName] !== undefined && !dayClosePayload[fieldName]) {
          dayClosePayload[fieldName] = parseFloat(dayCloseData[fieldName] || 0);
        }
      });

    //  console.log('Day close payload to be saved:', dayClosePayload);
      //console.log('Day close payload to be saved:', dayClosePayload);

      // Save to day_close_summary table
      const response = await axios.post(
        '/insertdata/day_close_summary',
        dayClosePayload,
        getHeaders()
      );

      if (response.data.msg === 'Data saved successfully' && response.data.id) {
        setIsDayClosed(true);
        setDayCloseData(prev => ({ ...prev, status: 'closed' }));
        toast.success('Day Close saved successfully!');
        setShowCloseModal(false);
        
        // Save cash drawer data if provided
        if (cashDrawerData.closingCash > 0 || cashDrawerData.openingCash > 0) {
          try {
           // alert("hit");
            const cashDrawerPayload = {
              open_date: selectedDate,
              opening_cash: parseFloat(cashDrawerData.openingCash || 0),
              closing_cash: parseFloat(cashDrawerData.closingCash || 0),
              expected_cash: parseFloat(cashDrawerData.openingCash || 0) + parseFloat(dayCloseData.cash_sales || 0),
              cash_difference: parseFloat(cashDrawerData.closingCash || 0) - (parseFloat(cashDrawerData.openingCash || 0) + parseFloat(dayCloseData.cash_sales || 0)),
              cash_in: parseFloat(cashDrawerData.cashIn || 0),
              cash_out: parseFloat(cashDrawerData.cashOut || 0),
              opened_by: getUserName(), // Set opened_by to current user
              closed_by: getUserName(),
              opening_time: new Date().toISOString(), // Set current time as opening time
              closing_time: new Date().toISOString(),
              status: 'closed',
              notes: cashDrawerData.notes || ''
            };

            console.log('Cash drawer payload:', cashDrawerPayload);

            const cashDrawerResponse = await axios.post(
              '/insertdata/cash_drawer',
              cashDrawerPayload,
              getHeaders()
            );

            if (cashDrawerResponse.data.msg === 'Data saved successfully' && cashDrawerResponse.data.id) {
              console.log('Cash drawer data saved successfully');
            } else {
              console.error('Failed to save cash drawer data:', cashDrawerResponse.data);
              toast.warning('Day close saved but cash drawer data failed to save');
            }
          } catch (cashDrawerError) {
            console.error('Error saving cash drawer data:', cashDrawerError);
            toast.warning('Day close saved but cash drawer data failed to save');
          }
        }
      } else {
        toast.error('Failed to save day close data');
      }
    } catch (error) {
      console.error('Error saving day close:', error);
      toast.error('Error saving day close data');
    } finally {
      setLoading(false);
    }
  };

  // Generate payment methods from fetched options
  const generatePaymentMethods = () => {
    // Map payment option names to correct database column names
    const paymentMethodMapping = {
      'qr code': 'qr_sales',
      'qr': 'qr_sales',
      'bank transfer': 'bank_transfer_sales',
      'credit card': 'card_sales',
      'debit card': 'card_sales'
    };
    
    return paymentOptions.map((option) => {
      const paymentConfig = getPaymentIcon(option.name);
      const optionName = option.name.toLowerCase();
      
      // Use mapped field name if available, otherwise create dynamic field name
      const key = paymentMethodMapping[optionName] || `${optionName.replace(/\s+/g, '_')}_sales`;
      
      console.log(`Payment Option: "${option.name}" -> Key: "${key}" -> Value: ${dayCloseData?.[key] || 0}`);
      
      return {
        key: key,
        label: option.name,
        icon: paymentConfig.icon,
        color: paymentConfig.color,
        gradient: paymentConfig.gradient
      };
    });
  };

  return (
    <Layout>
      <div className="day-close-container">
        <ToastContainer />
        
        <div className="page-header d-flex justify-content-between align-items-center mb-4">
          <div>
            <h6 className="page-title">
              <FaCalendarAlt className="me-2" />
              Day Close Summary
            </h6>
            <p className="text-muted">View and manage daily sales summary</p>
          </div>
          
          <div className="header-actions d-flex gap-2">
            <Form.Control
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="date-picker"
            />
            
            <Button 
              variant="outlined" 
              color="primary"
              onClick={() => setDetailedView(!detailedView)}
              size="small"
              startIcon={<Visibility />}
              sx={{ 
                textTransform: 'none',
                borderRadius: 2,
                px: 2
              }}
            >
              {detailedView ? 'Summary' : 'Details'}
            </Button>
            
            {/* <Button 
              variant="outlined" 
              color="warning"
              onClick={() => setShowDebugInfo(!showDebugInfo)}
              size="small"
              startIcon={<Visibility />}
              sx={{ 
                textTransform: 'none',
                borderRadius: 2,
                px: 2
              }}
            >
              Debug Info
            </Button> */}
            
            {/* Day Close Button - Only show if day is not closed */}
            {!isDayClosed && (
              <Button 
                variant="contained"
                color="success"
                onClick={() => setShowCloseModal(true)}
                disabled={loading || !dayCloseData}
                size="small"
                startIcon={<Lock />}
                sx={{ 
                  textTransform: 'none',
                  borderRadius: 2,
                  px: 2
                }}
              >
                Day Close
              </Button>
            )}
            
            {/* Sale Reconciliation Report Button */}
            <Button 
              variant="outlined"
              color="info"
              onClick={() => {
                setShowReconciliationReport(true);
                fetchReconciliationData();
              }}
              size="small"
              startIcon={<FaUniversity />}
              sx={{ 
                textTransform: 'none',
                borderRadius: 2,
                px: 2
              }}
            >
              Reconciliation Report
            </Button>
            
            {/* Cashier Summary Report Button */}
            <Button 
              variant="outlined"
              color="secondary"
              onClick={() => window.open('/reports/cashier-summary-report', '_blank')}
              size="small"
              startIcon={<Person />}
              sx={{ 
                textTransform: 'none',
                borderRadius: 2,
                px: 2
              }}
            >
              Cashier Report
            </Button>
            
            {/* Day Close Report Button */}
            <Button 
              variant="outlined"
              color="primary"
              onClick={() => setShowDayCloseReport(true)}
              size="small"
              startIcon={<Print />}
              sx={{ 
                textTransform: 'none',
                borderRadius: 2,
                px: 2
              }}
            >
              Day Close Report
            </Button>
            
            <Button 
              variant="outlined"
              color="warning"
              onClick={() => {
                setShowReconciliation(!showReconciliation);
                if (!showReconciliation) {
                  fetchReconciliationData();
                }
              }}
              size="small"
              startIcon={<Visibility />}
              sx={{ 
                textTransform: 'none',
                borderRadius: 2,
                px: 2
              }}
            >
              {showReconciliation ? 'Hide Reconciliation' : 'Sale Reconciliation'}
            </Button>
          </div>
        </div>

        {/* Debug Information Panel */}
        {showDebugInfo && (
          <Card className="mb-4 border-warning">
            <Card.Header className="bg-warning text-dark">
              <h6 className="mb-0">🔍 Debug Information</h6>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <h6>Database Query Info:</h6>
                  <ul>
                    <li><strong>Selected Date:</strong> {debugData.selectedDateFormatted}</li>
                    <li><strong>Query:</strong> {debugData.fetchQuery}</li>
                    <li><strong>Total Bills in DB:</strong> {debugData.allBillsCount}</li>
                    <li><strong>Bills for Selected Date:</strong> {debugData.billsForDate}</li>
                  </ul>
                </Col>
                <Col md={6}>
                  <h6>Sample Bills (Latest 5):</h6>
                  {debugData.sampleBills.length > 0 ? (
                    <div style={{ fontSize: '12px' }}>
                      {debugData.sampleBills.map((bill, index) => (
                        <div key={index} className="border-bottom pb-1 mb-1">
                          <strong>Bill #{bill.id}:</strong><br/>
                          Setup Date: {bill.setup_date}<br/>
                          Bill Date: {bill.bill_date}<br/>
                          Amount: ฿{bill.total_amount}<br/>
                          Created: {new Date(bill.created_at).toLocaleString()}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p>No bills found in database</p>
                  )}
                </Col>
              </Row>
            </Card.Body>
          </Card>
        )}

        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2">Loading day close data...</p>
          </div>
        ) : (
          <>
          

            {/* Summary Cards */}
            <Row className="mb-4">
              <Col lg={3} md={6} className="mb-3">
                <Card className="summary-card total-sales">
                  <Card.Body>
                    <div className="summary-icon">
                      <FaMoneyBillWave />
                    </div>
                    <div className="summary-content">
                      <div className="summary-amount">{formatCurrency(dayCloseData?.total_sales)}</div>
                      <div className="summary-label">Total Sales</div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              
              <Col lg={3} md={6} className="mb-3">
                <Card className="summary-card total-orders">
                  <Card.Body>
                    <div className="summary-icon">
                      <FaCalendarAlt />
                    </div>
                    <div className="summary-content">
                      <div className="summary-amount">{dayCloseData?.total_orders || 0}</div>
                      <div className="summary-label">Total Orders</div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              
              <Col lg={3} md={6} className="mb-3">
                <Card className="summary-card total-items">
                  <Card.Body>
                    <div className="summary-icon">
                      <FaQrcode />
                    </div>
                    <div className="summary-content">
                      <div className="summary-amount">{dayCloseData?.total_items_sold || 0}</div>
                      <div className="summary-label">Items Sold</div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              
              <Col lg={3} md={6} className="mb-3">
                <Card className="summary-card avg-order">
                  <Card.Body>
                    <div className="summary-icon">
                      <FaCreditCard />
                    </div>
                    <div className="summary-content">
                      <div className="summary-amount">
                        {formatCurrency(
                          dayCloseData?.total_orders > 0 
                            ? dayCloseData.total_sales / dayCloseData.total_orders 
                            : 0
                        )}
                      </div>
                      <div className="summary-label">Avg Order Value</div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Payment Methods Breakdown */}
            <Card className="mb-4 shadow-lg border-0">
              <Card.Header className="bg-gradient text-white" >
                <h5 className="mb-0 d-flex align-items-center">
                  <FaCreditCard className="me-2" />
                  Payment Methods Breakdown
                </h5>
                <small className="opacity-75">Daily payment collection by method</small>
              </Card.Header>
              <Card.Body className="p-4">
                {paymentOptions.length === 0 ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" className="mb-3" />
                    <p className="text-muted">Loading payment methods...</p>
                  </div>
                ) : (
                  <>
                    <Row>
                      {generatePaymentMethods().map((method) => (
                        <Col lg={4} md={6} sm={12} key={method.key} className="mb-4">
                          <div className="payment-method-card-minimal" style={{
                            background: 'white',
                            borderRadius: '12px',
                            padding: '24px',
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                            border: '1px solid #f0f0f0',
                            transition: 'all 0.3s ease',
                            position: 'relative',
                            overflow: 'hidden'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-4px)';
                            e.currentTarget.style.boxShadow = '0 8px 40px rgba(0, 0, 0, 0.12)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.08)';
                          }}>
                            {/* Minimal gradient accent */}
                            <div style={{
                              position: 'absolute',
                              top: '0',
                              left: '0',
                              right: '0',
                              height: '4px',
                              background: method.gradient,
                              borderRadius: '12px 12px 0 0'
                            }}></div>
                            
                            {/* Content */}
                            <div className="pt-2">
                              <div className="d-flex justify-content-between align-items-start mb-3">
                                <div className="payment-icon-minimal" style={{
                                  fontSize: '2rem',
                                  color: '#6c757d'
                                }}>
                                  {method.icon}
                                </div>
                                <div className="text-end">
                                  <small className="text-muted text-uppercase" style={{ 
                                    fontSize: '0.75rem',
                                    fontWeight: '500',
                                    letterSpacing: '0.5px'
                                  }}>
                                    Payment
                                  </small>
                                </div>
                              </div>
                              
                              <div>
                                <h3 className="mb-1 text-dark" style={{ 
                                  fontWeight: '600',
                                  fontSize: '1.6rem'
                                }}>
                                  {formatCurrency(dayCloseData?.[method.key] || 0)}
                                </h3>
                                <h6 className="mb-3 text-secondary" style={{ 
                                  fontWeight: '500',
                                  fontSize: '1rem'
                                }}>
                                  {method.label}
                                </h6>
                                
                                <div className="d-flex justify-content-between align-items-center">
                                  <small className="text-muted">
                                    Today's Collection
                                  </small>
                                  <div className="status-badge" style={{
                                    padding: '3px 8px',
                                    background: dayCloseData?.[method.key] > 0 ? '#e8f5e8' : '#f8f9fa',
                                    color: dayCloseData?.[method.key] > 0 ? '#28a745' : '#6c757d',
                                    borderRadius: '8px',
                                    fontSize: '0.75rem',
                                    fontWeight: '500'
                                  }}>
                                    {dayCloseData?.[method.key] > 0 ? '● Active' : '○ No transactions'}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </Col>
                      ))}
                    </Row>
                    
                    {/* Summary Footer */}
                    <div className="mt-4 pt-4 border-top" style={{ borderColor: '#e9ecef !important' }}>
                      <Row className="justify-content-center">
                        <Col md={5} lg={4} className="mb-3 mb-md-0">
                          <div className="d-flex align-items-center justify-content-center p-3" style={{
                            background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                            borderRadius: '12px',
                            border: '1px solid #dee2e6',
                            height: '100%',
                            minHeight: '80px'
                          }}>
                            <div className="summary-icon me-3" style={{
                              width: '48px',
                              height: '48px',
                              background: 'white',
                              borderRadius: '12px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#28a745',
                              fontSize: '1.5rem',
                              boxShadow: '0 2px 8px rgba(40, 167, 69, 0.2)',
                              flexShrink: 0
                            }}>
                              <FaMoneyBillWave />
                            </div>
                            <div className="text-center text-md-start">
                              <h5 className="mb-0 text-success" style={{ fontWeight: '600' }}>
                                {formatCurrency(dayCloseData?.total_sales || 0)}
                              </h5>
                              <small className="text-muted">Total Collections</small>
                            </div>
                          </div>
                        </Col>
                        <Col md={5} lg={4}>
                          <div className="d-flex align-items-center justify-content-center p-3" style={{
                            background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                            borderRadius: '12px',
                            border: '1px solid #dee2e6',
                            height: '100%',
                            minHeight: '80px'
                          }}>
                            <div className="summary-icon me-3" style={{
                              width: '48px',
                              height: '48px',
                              background: 'white',
                              borderRadius: '12px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#007bff',
                              fontSize: '1.5rem',
                              boxShadow: '0 2px 8px rgba(0, 123, 255, 0.2)',
                              flexShrink: 0
                            }}>
                              📊
                            </div>
                            <div className="text-center text-md-start">
                              <h5 className="mb-0 text-primary" style={{ fontWeight: '600' }}>
                                {dayCloseData?.total_orders || 0}
                              </h5>
                              <small className="text-muted">Total Orders</small>
                            </div>
                          </div>
                        </Col>
                      </Row>
                    </div>
                  </>
                )}
              </Card.Body>
            </Card>

            {/* Detailed View */}
            {detailedView && (
              <Card>
                <Card.Header>
                  <h5 className="mb-0">Sales Breakdown by Items</h5>
                </Card.Header>
                <Card.Body>
                  <Table striped bordered hover responsive>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Item Name</th>
                        <th>Quantity Sold</th>
                        <th>Total Amount</th>
                        <th>Orders</th>
                        <th>Avg Price</th>
                      </tr>
                    </thead>
                    <tbody>
                      {salesBreakdown.map((item, index) => (
                        <tr key={index}>
                          <td>{index + 1}</td>
                          <td>{item.item_name}</td>
                          <td>{item.total_quantity}</td>
                          <td>{formatCurrency(item.total_amount)}</td>
                          <td>{item.orders}</td>
                          <td>{formatCurrency(item.total_amount / item.total_quantity)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  
                  {salesBreakdown.length === 0 && (
                    <div className="text-center py-4">
                      <p className="text-muted">No sales data found for {selectedDate}</p>
                    </div>
                  )}
                </Card.Body>
              </Card>
            )}

            {/* Sale Reconciliation Section */}
            {showReconciliation && (
              <Card className="mt-4">
                <Card.Header className="bg-warning text-dark">
                  <h5 className="mb-0">
                    <FaUniversity className="me-2" />
                    Sale Reconciliation Report
                  </h5>
                  <small>Compare sales data across all systems for accuracy</small>
                </Card.Header>
                <Card.Body>
                  {loading ? (
                    <div className="text-center py-4">
                      <Spinner animation="border" variant="warning" />
                      <p className="mt-2">Loading reconciliation data...</p>
                    </div>
                  ) : (
                    <>
                      {/* Reconciliation Status */}
                      <Alert variant={reconciliationData.isReconciled ? "success" : "danger"} className="mb-4">
                        <div className="d-flex align-items-center">
                          <div className="me-3">
                            {reconciliationData.isReconciled ? "✅" : "⚠️"}
                          </div>
                          <div>
                            <strong>
                              {reconciliationData.isReconciled ? 
                                "All Systems Reconciled" : 
                                `${reconciliationData.discrepancies.length} Discrepancies Found`}
                            </strong>
                            <br />
                            <small>
                              {reconciliationData.isReconciled ? 
                                "All sales data matches across Day Close, Cashier Summaries, and Bills" :
                                "Please review the discrepancies below and investigate any differences"}
                            </small>
                          </div>
                        </div>
                      </Alert>

                      {/* Comparison Table */}
                      <Row>
                        <Col lg={12}>
                          <h6>Sales Data Comparison</h6>
                          <div style={{ overflowX: 'auto', overflowY: 'visible', WebkitOverflowScrolling: 'touch', width: '100%' }}>
                            <Table striped bordered hover className="mb-4" style={{ minWidth: '1000px', width: '100%', marginBottom: 0 }}>
                            <thead className="table-dark">
                              <tr>
                                <th>Payment Method</th>
                                <th>Cashier Summaries</th>
                                <th>Bills Total</th>
                                <th>Difference</th>
                                <th>Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td><strong>Total Sales</strong></td>
                                <td>
                                  {formatCurrency(
                                    reconciliationData.cashierSummaries.reduce((sum, cashier) => 
                                      sum + parseFloat(cashier.total_sales || 0), 0
                                    )
                                  )}
                                </td>
                                <td>{formatCurrency(reconciliationData.billsSummary?.total_sales || 0)}</td>
                                <td>
                                  {(() => {
                                    const cashier = reconciliationData.cashierSummaries.reduce((sum, cashier) => 
                                      sum + parseFloat(cashier.total_sales || 0), 0
                                    );
                                    const bills = parseFloat(reconciliationData.billsSummary?.total_sales || 0);
                                    const diff = cashier - bills;
                                    return formatCurrency(diff);
                                  })()}
                                </td>
                                <td style={{
                                  backgroundColor: (() => {
                                    const cashier = reconciliationData.cashierSummaries.reduce((sum, cashier) => 
                                      sum + parseFloat(cashier.total_sales || 0), 0
                                    );
                                    const bills = parseFloat(reconciliationData.billsSummary?.total_sales || 0);
                                    const diff = Math.abs(cashier - bills);
                                    return diff <= 0.01 ? '#d4edda' : '#f8d7da';
                                  })()
                                }}>
                                  {(() => {
                                    const cashier = reconciliationData.cashierSummaries.reduce((sum, cashier) => 
                                      sum + parseFloat(cashier.total_sales || 0), 0
                                    );
                                    const bills = parseFloat(reconciliationData.billsSummary?.total_sales || 0);
                                    const diff = Math.abs(cashier - bills);
                                    return diff <= 0.01 ? 
                                      <span className="badge bg-success">✓ Match</span> : 
                                      <span className="badge bg-danger">✗ Mismatch ({formatCurrency(diff)})</span>;
                                  })()}
                                </td>
                              </tr>
                              <tr>
                                <td>Cash Sales</td>
                                <td>
                                  {formatCurrency(
                                    reconciliationData.cashierSummaries.reduce((sum, cashier) => 
                                      sum + parseFloat(cashier.cash_total || 0), 0
                                    )
                                  )}
                                </td>
                                <td>{formatCurrency(reconciliationData.billsSummary?.cash_sales || 0)}</td>
                                <td>
                                  {(() => {
                                    const cashier = reconciliationData.cashierSummaries.reduce((sum, cashier) => 
                                      sum + parseFloat(cashier.cash_total || 0), 0
                                    );
                                    const bills = parseFloat(reconciliationData.billsSummary?.cash_sales || 0);
                                    const diff = cashier - bills;
                                    return formatCurrency(diff);
                                  })()}
                                </td>
                                <td style={{
                                  backgroundColor: (() => {
                                    const cashier = reconciliationData.cashierSummaries.reduce((sum, cashier) => 
                                      sum + parseFloat(cashier.cash_total || 0), 0
                                    );
                                    const bills = parseFloat(reconciliationData.billsSummary?.cash_sales || 0);
                                    const diff = Math.abs(cashier - bills);
                                    return diff <= 0.01 ? '#d4edda' : '#f8d7da';
                                  })()
                                }}>
                                  {(() => {
                                    const cashier = reconciliationData.cashierSummaries.reduce((sum, cashier) => 
                                      sum + parseFloat(cashier.cash_total || 0), 0
                                    );
                                    const bills = parseFloat(reconciliationData.billsSummary?.cash_sales || 0);
                                    const diff = Math.abs(cashier - bills);
                                    return diff <= 0.01 ? 
                                      <span className="badge bg-success">✓ Match</span> : 
                                      <span className="badge bg-danger">⚠ Diff ({formatCurrency(diff)})</span>;
                                  })()}
                                </td>
                              </tr>
                              <tr>
                                <td>UPI Sales</td>
                                <td>
                                  {formatCurrency(
                                    reconciliationData.cashierSummaries.reduce((sum, cashier) => 
                                      sum + parseFloat(cashier.upi_total || 0), 0
                                    )
                                  )}
                                </td>
                                <td>{formatCurrency(reconciliationData.billsSummary?.upi_sales || 0)}</td>
                                <td>
                                  {(() => {
                                    const cashier = reconciliationData.cashierSummaries.reduce((sum, cashier) => 
                                      sum + parseFloat(cashier.upi_total || 0), 0
                                    );
                                    const bills = parseFloat(reconciliationData.billsSummary?.upi_sales || 0);
                                    const diff = cashier - bills;
                                    return formatCurrency(diff);
                                  })()}
                                </td>
                                <td style={{
                                  backgroundColor: (() => {
                                    const cashier = reconciliationData.cashierSummaries.reduce((sum, cashier) => 
                                      sum + parseFloat(cashier.upi_total || 0), 0
                                    );
                                    const bills = parseFloat(reconciliationData.billsSummary?.upi_sales || 0);
                                    const diff = Math.abs(cashier - bills);
                                    return diff <= 0.01 ? '#d4edda' : '#f8d7da';
                                  })()
                                }}>
                                  {(() => {
                                    const cashier = reconciliationData.cashierSummaries.reduce((sum, cashier) => 
                                      sum + parseFloat(cashier.upi_total || 0), 0
                                    );
                                    const bills = parseFloat(reconciliationData.billsSummary?.upi_sales || 0);
                                    const diff = Math.abs(cashier - bills);
                                    return diff <= 0.01 ? 
                                      <span className="badge bg-success">✓ Match</span> : 
                                      <span className="badge bg-danger">⚠ Diff ({formatCurrency(diff)})</span>;
                                  })()}
                                </td>
                              </tr>
                              <tr>
                                <td>Card Sales</td>
                                <td>
                                  {formatCurrency(
                                    reconciliationData.cashierSummaries.reduce((sum, cashier) => 
                                      sum + parseFloat(cashier.card_total || 0), 0
                                    )
                                  )}
                                </td>
                                <td>{formatCurrency(reconciliationData.billsSummary?.card_sales || 0)}</td>
                                <td>
                                  {(() => {
                                    const cashier = reconciliationData.cashierSummaries.reduce((sum, cashier) => 
                                      sum + parseFloat(cashier.card_total || 0), 0
                                    );
                                    const bills = parseFloat(reconciliationData.billsSummary?.card_sales || 0);
                                    const diff = cashier - bills;
                                    return formatCurrency(diff);
                                  })()}
                                </td>
                                <td style={{
                                  backgroundColor: (() => {
                                    const cashier = reconciliationData.cashierSummaries.reduce((sum, cashier) => 
                                      sum + parseFloat(cashier.card_total || 0), 0
                                    );
                                    const bills = parseFloat(reconciliationData.billsSummary?.card_sales || 0);
                                    const diff = Math.abs(cashier - bills);
                                    return diff <= 0.01 ? '#d4edda' : '#f8d7da';
                                  })()
                                }}>
                                  {(() => {
                                    const cashier = reconciliationData.cashierSummaries.reduce((sum, cashier) => 
                                      sum + parseFloat(cashier.card_total || 0), 0
                                    );
                                    const bills = parseFloat(reconciliationData.billsSummary?.card_sales || 0);
                                    const diff = Math.abs(cashier - bills);
                                    return diff <= 0.01 ? 
                                      <span className="badge bg-success">✓ Match</span> : 
                                      <span className="badge bg-danger">⚠ Diff ({formatCurrency(diff)})</span>;
                                  })()}
                                </td>
                              </tr>
                              <tr>
                                <td>QR Sales</td>
                                <td>
                                  {formatCurrency(
                                    reconciliationData.cashierSummaries.reduce((sum, cashier) => 
                                      sum + parseFloat(cashier.qr_total || 0), 0
                                    )
                                  )}
                                </td>
                                <td>{formatCurrency(reconciliationData.billsSummary?.qr_sales || 0)}</td>
                                <td>
                                  {(() => {
                                    const cashier = reconciliationData.cashierSummaries.reduce((sum, cashier) => 
                                      sum + parseFloat(cashier.qr_total || 0), 0
                                    );
                                    const bills = parseFloat(reconciliationData.billsSummary?.qr_sales || 0);
                                    const diff = cashier - bills;
                                    return formatCurrency(diff);
                                  })()}
                                </td>
                                <td style={{
                                  backgroundColor: (() => {
                                    const cashier = reconciliationData.cashierSummaries.reduce((sum, cashier) => 
                                      sum + parseFloat(cashier.qr_total || 0), 0
                                    );
                                    const bills = parseFloat(reconciliationData.billsSummary?.qr_sales || 0);
                                    const diff = Math.abs(cashier - bills);
                                    return diff <= 0.01 ? '#d4edda' : '#f8d7da';
                                  })()
                                }}>
                                  {(() => {
                                    const cashier = reconciliationData.cashierSummaries.reduce((sum, cashier) => 
                                      sum + parseFloat(cashier.qr_total || 0), 0
                                    );
                                    const bills = parseFloat(reconciliationData.billsSummary?.qr_sales || 0);
                                    const diff = Math.abs(cashier - bills);
                                    return diff <= 0.01 ? 
                                      <span className="badge bg-success">✓ Match</span> : 
                                      <span className="badge bg-danger">⚠ Diff ({formatCurrency(diff)})</span>;
                                  })()}
                                </td>
                              </tr>
                              <tr>
                                <td>Entertainment Sales</td>
                                <td>
                                  {formatCurrency(
                                    reconciliationData.cashierSummaries.reduce((sum, cashier) => {
                                      const paymentBreakdown = typeof cashier.payment_breakdown === 'string' 
                                        ? JSON.parse(cashier.payment_breakdown || '{}') 
                                        : cashier.payment_breakdown || {};
                                      return sum + parseFloat(paymentBreakdown.entertainment || 0);
                                    }, 0)
                                  )}
                                </td>
                                <td>{formatCurrency(reconciliationData.billsSummary?.entertainment_sales || 0)}</td>
                                <td>
                                  {(() => {
                                    const cashier = reconciliationData.cashierSummaries.reduce((sum, cashier) => {
                                      const paymentBreakdown = typeof cashier.payment_breakdown === 'string' 
                                        ? JSON.parse(cashier.payment_breakdown || '{}') 
                                        : cashier.payment_breakdown || {};
                                      return sum + parseFloat(paymentBreakdown.entertainment || 0);
                                    }, 0);
                                    const bills = parseFloat(reconciliationData.billsSummary?.entertainment_sales || 0);
                                    const diff = cashier - bills;
                                    return formatCurrency(diff);
                                  })()}
                                </td>
                                <td style={{
                                  backgroundColor: (() => {
                                    const cashier = reconciliationData.cashierSummaries.reduce((sum, cashier) => {
                                      const paymentBreakdown = typeof cashier.payment_breakdown === 'string' 
                                        ? JSON.parse(cashier.payment_breakdown || '{}') 
                                        : cashier.payment_breakdown || {};
                                      return sum + parseFloat(paymentBreakdown.entertainment || 0);
                                    }, 0);
                                    const bills = parseFloat(reconciliationData.billsSummary?.entertainment_sales || 0);
                                    const diff = Math.abs(cashier - bills);
                                    return diff <= 0.01 ? '#d4edda' : '#f8d7da';
                                  })()
                                }}>
                                  {(() => {
                                    const cashier = reconciliationData.cashierSummaries.reduce((sum, cashier) => {
                                      const paymentBreakdown = typeof cashier.payment_breakdown === 'string' 
                                        ? JSON.parse(cashier.payment_breakdown || '{}') 
                                        : cashier.payment_breakdown || {};
                                      return sum + parseFloat(paymentBreakdown.entertainment || 0);
                                    }, 0);
                                    const bills = parseFloat(reconciliationData.billsSummary?.entertainment_sales || 0);
                                    const diff = Math.abs(cashier - bills);
                                    return diff <= 0.01 ? 
                                      <span className="badge bg-success">✓ Match</span> : 
                                      <span className="badge bg-danger">⚠ Diff ({formatCurrency(diff)})</span>;
                                  })()}
                                </td>
                              </tr>
                            </tbody>
                          </Table>
                          </div>
                        </Col>
                      </Row>

                      {/* Discrepancies Details */}
                      {reconciliationData.discrepancies.length > 0 && (
                        <Row>
                          <Col lg={12}>
                            <h6>Discrepancy Details</h6>
                            <Table striped bordered hover responsive>
                              <thead className="table-warning">
                                <tr>
                                  <th>Type</th>
                                  <th>Source 1</th>
                                  <th>Source 2</th>
                                  <th>Amount 1</th>
                                  <th>Amount 2</th>
                                  <th>Difference</th>
                                  <th>Severity</th>
                                </tr>
                              </thead>
                              <tbody>
                                {reconciliationData.discrepancies.map((discrepancy, index) => (
                                  <tr key={index}>
                                    <td>{discrepancy.type.replace(/_/g, ' ').toUpperCase()}</td>
                                    <td>{discrepancy.source1}</td>
                                    <td>{discrepancy.source2}</td>
                                    <td>{formatCurrency(discrepancy.amount1)}</td>
                                    <td>{formatCurrency(discrepancy.amount2)}</td>
                                    <td className={discrepancy.difference > 0 ? 'text-success' : 'text-danger'}>
                                      {formatCurrency(discrepancy.difference)}
                                    </td>
                                    <td>
                                      <span className={`badge ${
                                        discrepancy.severity === 'high' ? 'bg-danger' : 
                                        discrepancy.severity === 'medium' ? 'bg-warning' : 'bg-info'
                                      }`}>
                                        {discrepancy.severity}
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </Col>
                        </Row>
                      )}

                      {/* Cashier Summary Details */}
                      {reconciliationData.cashierSummaries.length > 0 && (
                        <Row className="mt-4">
                          <Col lg={12}>
                            <h6>Individual Cashier Summaries</h6>
                            <div style={{ overflowX: 'auto', overflowY: 'visible', WebkitOverflowScrolling: 'touch', width: '100%' }}>
                              <Table striped bordered hover style={{ minWidth: '1100px', width: '100%', marginBottom: 0 }}>
                              <thead className="table-info">
                                <tr>
                                  <th>Cashier ID</th>
                                  <th>Cashier Name</th>
                                  <th>Total Sales</th>
                                  <th>Cash Total</th>
                                  <th>Transactions</th>
                                  <th>Session Duration</th>
                                  <th>Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                {reconciliationData.cashierSummaries.map((cashier, index) => (
                                  <tr key={index}>
                                    <td>{cashier.cashier_id}</td>
                                    <td>{cashier.cashier_name}</td>
                                    <td>{formatCurrency(cashier.total_sales)}</td>
                                    <td>{formatCurrency(cashier.cash_total)}</td>
                                    <td>{cashier.total_transactions}</td>
                                    <td>{cashier.session_duration_minutes} min</td>
                                    <td>
                                      <span className={`badge ${
                                        cashier.status === 'reconciled' ? 'bg-success' : 
                                        cashier.status === 'closed' ? 'bg-warning' : 'bg-primary'
                                      }`}>
                                        {cashier.status}
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                            </div>
                          </Col>
                        </Row>
                      )}

                      {/* Action Buttons */}
                      <div className="d-flex justify-content-end mt-4">
                        <Button 
                          variant="outline-secondary"
                          size="sm"
                          onClick={fetchReconciliationData}
                          className="me-2"
                        >
                          Refresh Data
                        </Button>
                        {!reconciliationData.isReconciled && (
                          <Button 
                            variant="warning"
                            size="sm"
                            onClick={() => {
                              toast.info('Manual reconciliation process can be implemented here');
                            }}
                          >
                            Force Reconciliation
                          </Button>
                        )}
                      </div>
                    </>
                  )}
                </Card.Body>
              </Card>
            )}
          </>
        )}

        {/* Day Close Modal */}
        <Modal 
          show={showCloseModal} 
          onHide={() => setShowCloseModal(false)} 
          centered
          className="day-close-modal"
          dialogClassName="modal-50w modal-dialog-scrollable"
          scrollable
        >
          <Modal.Header closeButton>
            <Modal.Title>Close Day - {selectedDate}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Alert variant="warning">
              <strong>Warning:</strong> Once you close the day, you cannot modify the sales data for this date.
            </Alert>
            
            <Form>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Opening Cash Amount</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      value={cashDrawerData.openingCash}
                      onChange={(e) => setCashDrawerData({
                        ...cashDrawerData,
                        openingCash: e.target.value
                      })}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Closing Cash Amount</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      value={cashDrawerData.closingCash}
                      onChange={(e) => setCashDrawerData({
                        ...cashDrawerData,
                        closingCash: e.target.value
                      })}
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Cash In (Additional)</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      value={cashDrawerData.cashIn}
                      onChange={(e) => setCashDrawerData({
                        ...cashDrawerData,
                        cashIn: e.target.value
                      })}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Cash Out (Expenses)</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      value={cashDrawerData.cashOut}
                      onChange={(e) => setCashDrawerData({
                        ...cashDrawerData,
                        cashOut: e.target.value
                      })}
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              <Form.Group className="mb-3">
                <Form.Label>Notes</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={cashDrawerData.notes}
                  onChange={(e) => setCashDrawerData({
                    ...cashDrawerData,
                    notes: e.target.value
                  })}
                  placeholder="Add any notes for this day close..."
                />
              </Form.Group>
              
              <div className="cash-summary p-3 bg-light rounded">
                <h6>Cash Summary</h6>
                <div className="d-flex justify-content-between">
                  <span>Expected Cash:</span>
                  <span>{formatCurrency(dayCloseData?.cash_sales || 0)}</span>
                </div>
                <div className="d-flex justify-content-between">
                  <span>Actual Cash:</span>
                  <span>{formatCurrency(cashDrawerData.closingCash)}</span>
                </div>
                <div className="d-flex justify-content-between border-top pt-2">
                  <strong>Difference:</strong>
                  <strong className={
                    (cashDrawerData.closingCash - (dayCloseData?.cash_sales || 0)) >= 0 
                      ? 'text-success' 
                      : 'text-danger'
                  }>
                    {formatCurrency(cashDrawerData.closingCash - (dayCloseData?.cash_sales || 0))}
                  </strong>
                </div>
              </div>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button 
              variant="outlined"
              color="secondary"
              onClick={() => setShowCloseModal(false)}
              startIcon={<Cancel />}
              sx={{ 
                textTransform: 'none',
                borderRadius: 2,
                px: 3
              }}
            >
              Cancel
            </Button>
            <Button 
              variant="contained"
              color="error"
              onClick={saveDayClose}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <LockClock />}
              sx={{ 
                textTransform: 'none',
                borderRadius: 2,
                px: 3,
                ml: 2
              }}
            >
              Close Day
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Sale Reconciliation Report Modal */}
        <Modal 
          show={showReconciliationReport} 
          onHide={() => setShowReconciliationReport(false)}
          centered
          className="reconciliation-report-modal"
          size="xl"
          dialogClassName="modal-dialog-centered modal-dialog-scrollable modal-90w"
        >
          <Modal.Header closeButton className="bg-info text-white">
            <Modal.Title style={{ fontSize: 'clamp(1rem, 2.5vw, 1.5rem)' }}>
              <FaUniversity className="me-2" />
              Sale Reconciliation Report - {selectedDate}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-3 p-md-4" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            {loading ? (
              <div className="text-center py-4">
                <CircularProgress size={40} />
                <p className="mt-2">Loading reconciliation data...</p>
              </div>
            ) : (
              <div className="reconciliation-report-content">
                <div className="row mb-4 g-3">
                  <div className="col-12 col-md-4">
                    <div className="summary-card bg-primary text-white p-3 rounded">
                      <h6 style={{ fontSize: 'clamp(0.875rem, 2vw, 1rem)' }}>Day Close Summary</h6>
                      <p className="mb-0" style={{ fontSize: 'clamp(0.75rem, 1.5vw, 0.875rem)' }}>
                        Total: {formatCurrency(reconciliationData.dayCloseData?.total_sales || 0)}
                      </p>
                    </div>
                  </div>
                  <div className="col-12 col-md-4">
                    <div className="summary-card bg-success text-white p-3 rounded">
                      <h6 style={{ fontSize: 'clamp(0.875rem, 2vw, 1rem)' }}>Cashier Summaries</h6>
                      <p className="mb-0" style={{ fontSize: 'clamp(0.75rem, 1.5vw, 0.875rem)' }}>
                        Total: {formatCurrency(reconciliationData.cashierSummaries?.reduce((sum, cashier) => sum + parseFloat(cashier.total_sales || 0), 0) || 0)}
                      </p>
                    </div>
                  </div>
                  <div className="col-12 col-md-4">
                    <div className="summary-card bg-warning text-white p-3 rounded">
                      <h6 style={{ fontSize: 'clamp(0.875rem, 2vw, 1rem)' }}>Bills Summary</h6>
                      <p className="mb-0" style={{ fontSize: 'clamp(0.75rem, 1.5vw, 0.875rem)' }}>
                        Total: {formatCurrency(reconciliationData.billsSummary?.total_sales || 0)}
                      </p>
                    </div>
                  </div>
                </div>

                {reconciliationData.discrepancies && reconciliationData.discrepancies.length > 0 ? (
                  <div className="alert alert-danger">
                    <h6 style={{ fontSize: 'clamp(0.875rem, 2vw, 1rem)' }}>Discrepancies Found ({reconciliationData.discrepancies.length})</h6>
                    <div className="table-responsive">
                      <Table striped bordered hover responsive="sm" style={{ fontSize: 'clamp(0.75rem, 1.5vw, 0.875rem)' }}>
                        <thead>
                          <tr>
                            <th>Type</th>
                            <th>Source 1</th>
                            <th>Source 2</th>
                            <th>Amount 1</th>
                            <th>Amount 2</th>
                            <th>Difference</th>
                            <th>Severity</th>
                          </tr>
                        </thead>
                        <tbody>
                          {reconciliationData.discrepancies.map((disc, index) => (
                            <tr key={index}>
                              <td>{disc.type.replace('_', ' ')}</td>
                              <td>{disc.source1}</td>
                              <td>{disc.source2}</td>
                              <td>{formatCurrency(disc.amount1)}</td>
                              <td>{formatCurrency(disc.amount2)}</td>
                              <td className={disc.difference > 0 ? 'text-success' : 'text-danger'}>
                                {formatCurrency(disc.difference)}
                              </td>
                              <td>
                                <span className={`badge bg-${disc.severity === 'high' ? 'danger' : disc.severity === 'medium' ? 'warning' : 'info'}`}>
                                  {disc.severity}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </div>
                ) : (
                  <div className="alert alert-success">
                    <h6 style={{ fontSize: 'clamp(0.875rem, 2vw, 1rem)' }}>✅ All Reconciled</h6>
                    <p className="mb-0" style={{ fontSize: 'clamp(0.75rem, 1.5vw, 0.875rem)' }}>No discrepancies found between data sources.</p>
                  </div>
                )}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer className="d-flex flex-wrap gap-2 justify-content-end">
            <Button 
              variant="outlined"
              color="primary"
              onClick={() => exportReconciliationToPDF()}
              startIcon={<Print />}
              sx={{ textTransform: 'none', fontSize: 'clamp(0.75rem, 1.5vw, 0.875rem)' }}
              size="small"
            >
              Export PDF
            </Button>
            <Button 
              variant="outlined"
              color="secondary"
              onClick={() => setShowReconciliationReport(false)}
              sx={{ textTransform: 'none', fontSize: 'clamp(0.75rem, 1.5vw, 0.875rem)' }}
              size="small"
            >
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Day Close Report Modal */}
        <Modal 
          show={showDayCloseReport} 
          onHide={() => setShowDayCloseReport(false)} 
          centered
          className="day-close-report-modal"
          dialogClassName="modal-50w"
        >
          <Modal.Header closeButton className="bg-primary text-white">
            <Modal.Title>
              <Print className="me-2" />
              Day Close Report - {selectedDate}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            <div className="day-close-report-content">
              {/* Report Header */}
              <div className="report-header text-center mb-4 border-bottom pb-3">
                <h4>View Park</h4>
                <h5>Day Close Summary Report</h5>
                <p className="text-muted">
                  Date: {new Date(selectedDate).toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </p>
              </div>

              {/* Sales Summary */}
              <div className="row mb-4">
                <div className="col-md-3">
                  <div className="report-metric bg-light p-3 rounded text-center">
                    <h6 className="text-primary">Total Sales</h6>
                    <h4>{formatCurrency(dayCloseData?.total_sales || 0)}</h4>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="report-metric bg-light p-3 rounded text-center">
                    <h6 className="text-success">Total Orders</h6>
                    <h4>{dayCloseData?.total_orders || 0}</h4>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="report-metric bg-light p-3 rounded text-center">
                    <h6 className="text-info">Items Sold</h6>
                    <h4>{dayCloseData?.total_items_sold || 0}</h4>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="report-metric bg-light p-3 rounded text-center">
                    <h6 className="text-warning">Avg Order</h6>
                    <h4>
                      {formatCurrency(
                        dayCloseData?.total_orders > 0 
                          ? dayCloseData.total_sales / dayCloseData.total_orders 
                          : 0
                      )}
                    </h4>
                  </div>
                </div>
              </div>

              {/* Payment Methods Breakdown */}
              <div className="mb-4">
                <h6 className="border-bottom pb-2">Payment Methods Breakdown</h6>
                <div className="row">
                  {generatePaymentMethods().map((method, index) => (
                    <div key={index} className="col-md-4 mb-3">
                      <div className="payment-method-card border rounded p-3">
                        <div className="d-flex align-items-center">
                          <div className="payment-icon me-3">
                            {getPaymentIcon(method.label).icon}
                          </div>
                          <div>
                            <h6 className="mb-1">{method.label}</h6>
                            <h5 className="mb-0">{formatCurrency(dayCloseData?.[method.key] || 0)}</h5>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Item-wise Sales */}
              {salesBreakdown && salesBreakdown.length > 0 && (
                <div className="mb-4">
                  <h6 className="border-bottom pb-2">Top Selling Items</h6>
                  <Table striped bordered hover responsive>
                    <thead>
                      <tr>
                        <th>S.No</th>
                        <th>Item Name</th>
                        <th>Quantity Sold</th>
                        <th>Total Amount</th>
                        <th>No. of Orders</th>
                        <th>Avg Price</th>
                      </tr>
                    </thead>
                    <tbody>
                      {salesBreakdown.slice(0, 10).map((item, index) => (
                        <tr key={index}>
                          <td>{index + 1}</td>
                          <td>{item.item_name}</td>
                          <td>{item.total_quantity}</td>
                          <td>{formatCurrency(item.total_amount)}</td>
                          <td>{item.orders}</td>
                          <td>{formatCurrency(item.total_amount / item.total_quantity)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}

              {/* Financial Summary */}
              <div className="financial-summary bg-light p-3 rounded">
                <h6 className="mb-3">Financial Summary</h6>
                <div className="row">
                  <div className="col-md-6">
                    <p><strong>Gross Sales:</strong> {formatCurrency(dayCloseData?.total_sales || 0)}</p>
                    <p><strong>Tax Amount:</strong> {formatCurrency(dayCloseData?.tax_amount || 0)}</p>
                    <p><strong>Discount Amount:</strong> {formatCurrency(dayCloseData?.discount_amount || 0)}</p>
                  </div>
                  <div className="col-md-6">
                    <p><strong>Net Sales:</strong> {formatCurrency(dayCloseData?.net_sales || 0)}</p>
                    <p><strong>Status:</strong> 
                      <span className={`ms-2 badge bg-${isDayClosed ? 'success' : 'warning'}`}>
                        {isDayClosed ? 'Closed' : 'Open'}
                      </span>
                    </p>
                    <p><strong>Closed By:</strong> {dayCloseData?.closed_by || 'N/A'}</p>
                  </div>
                </div>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button 
              variant="outlined"
              color="primary"
              onClick={() => exportDayCloseToPDF()}
              startIcon={<Print />}
              sx={{ textTransform: 'none' }}
            >
              Export PDF
            </Button>
            <Button 
              variant="outlined"
              color="secondary"
              onClick={() => setShowDayCloseReport(false)}
              sx={{ textTransform: 'none' }}
            >
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </Layout>
  );
}
